/**
 * Resolve a custom field value row under a browsing-scope workspace lens.
 *
 * Mirrors Database `resolveForWorkspace`, with archived awareness so an archived
 * scoped row cannot hide a live global fallback.
 *
 * Same rules for top-level `custom_fields` and nested group member rows
 * (`custom_fieldgroups[].custom_fields`).
 */

export type CustomFieldValueRow = {
  id?: number | null;
  custom_field_definition_id?: number;
  enterprise_workspace_id?: number | null;
  archived?: boolean | null;
  [key: string]: unknown;
};

function isLive(row: CustomFieldValueRow): boolean {
  return row.archived !== true;
}

/** Normalize workspace ids so `"2"` and `2` match the same scope. */
export function normalizeCustomFieldWorkspaceId(
  workspaceId: unknown
): number | null {
  if (workspaceId == null || workspaceId === '') return null;
  const n = Number(workspaceId);
  return Number.isFinite(n) ? n : null;
}

function workspaceOf(row: CustomFieldValueRow): number | null {
  return normalizeCustomFieldWorkspaceId(row.enterprise_workspace_id);
}

/**
 * @param rows full `custom_fields[]` (or candidates for one definition)
 * @param definitionId definition to resolve
 * @param workspaceId browsing lens — `null` = global only
 */
export function resolveCustomFieldValue<T extends CustomFieldValueRow>(
  rows: T[] | null | undefined,
  definitionId: number,
  workspaceId: number | null
): T | null {
  const candidates = (rows ?? []).filter(
    row => row.custom_field_definition_id === definitionId
  );
  return resolveCustomFieldCandidates(candidates, workspaceId);
}

/**
 * Same overlay rules when candidates are already filtered to one definition.
 */
export function resolveCustomFieldCandidates<T extends CustomFieldValueRow>(
  candidates: T[] = [],
  workspaceId: number | null = null
): T | null {
  const lens = normalizeCustomFieldWorkspaceId(workspaceId);
  const live = candidates.filter(isLive);
  if (lens != null) {
    const scoped = live.find(row => workspaceOf(row) === lens);
    if (scoped) return scoped;
  }
  return live.find(row => workspaceOf(row) === null) ?? null;
}

/**
 * Find the same-scope row for write (including archived, for unique-slot reuse).
 * Does not fall back to the other scope — caller must insert if missing.
 */
export function findSameScopeCustomFieldRow<T extends CustomFieldValueRow>(
  candidates: T[],
  workspaceId: number | null
): T | null {
  const lens = normalizeCustomFieldWorkspaceId(workspaceId);
  return candidates.find(row => workspaceOf(row) === lens) ?? null;
}

/**
 * Preserve bound form row identity (`id`, workspace stamp) when react-form's
 * fieldApi.value is empty/partial on first keystroke.
 */
export function mergeCustomFieldFormValue<T extends Record<string, unknown>>(
  boundValue: T | null | undefined,
  fieldApiValue: T | null | undefined,
  patch: Partial<T>
): T {
  return {
    ...(boundValue ?? ({} as T)),
    ...(fieldApiValue ?? ({} as T)),
    ...patch
  };
}
